import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotificationService } from '../../../_core/_services/notification.service';
import { SupportService } from '../../../_core/_services/support/support.service';
import {
  SupportTicketDetailsDto,
  SupportTicketStatus
} from '../../../_core/_model/support/support';

@Component({
  selector: 'app-admin-support-ticket-detail',
  standalone: false,
  templateUrl: './admin-support-ticket-detail.html',
  styleUrl: './admin-support-ticket-detail.css'
})
export class AdminSupportTicketDetail implements OnInit {
  loading = false;
  sending = false;
  savingStatus = false;
  reactivatingAccount = false;

  ticket: SupportTicketDetailsDto | null = null;
  message = '';
  isInternalNote = false;
  reactivationReason = '';
  selectedStatus: SupportTicketStatus | '' = '';

  readonly statuses: SupportTicketStatus[] = [
    'Open',
    'InProgress',
    'WaitingForUser',
    'WaitingForAdmin',
    'Resolved',
    'Closed'
  ];

  constructor(
    private route: ActivatedRoute,
    private supportService: SupportService,
    private notifyService: NotificationService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadTicket(id);
  }

  loadTicket(id: string): void {
    this.loading = true;

    this.supportService.getAdminTicket(id).subscribe({
      next: (res) => {
        this.ticket = res.data || null;
        this.selectedStatus = this.ticket?.status || '';
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.message || 'Failed to load support ticket.',
          'Error'
        );
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  sendMessage(): void {
    if (!this.ticket || !this.message.trim() || this.sending) return;

    this.sending = true;

    this.supportService
      .addAdminTicketMessage(this.ticket.id, this.message.trim(), this.isInternalNote)
      .subscribe({
        next: (res) => {
          const msg = res.data;
          if (msg && this.ticket) {
            this.ticket.messages.push(msg);
            this.ticket.lastUpdatedAt = msg.createdAt;
          }

          this.notifyService.showSuccess(
            res.message || 'Message added successfully.',
            'Success'
          );

          this.message = '';
          this.isInternalNote = false;
        },
        error: (err) => {
          this.notifyService.showError(
            err?.error?.message || 'Failed to send message.',
            'Error'
          );
          this.sending = false;
        },
        complete: () => {
          this.sending = false;
        }
      });
  }

  isReactivationTicket(): boolean {
    return !!this.ticket &&
      this.ticket.category === 'Account' &&
      this.ticket.source === 'chatbot-reactivation' &&
      !!this.ticket.userId &&
      this.ticket.status !== 'Resolved' &&
      this.ticket.status !== 'Closed';
  }

  reactivateAccount(): void {
    if (!this.ticket || !this.isReactivationTicket() || this.reactivatingAccount) return;

    const approved = window.confirm(
      'Reactivate this user account and resolve the support ticket?'
    );

    if (!approved) return;

    this.reactivatingAccount = true;
    this.supportService
      .reactivateAccountFromTicket(this.ticket.id, this.reactivationReason)
      .subscribe({
        next: (res) => {
          this.ticket = res.data || this.ticket;
          this.selectedStatus = this.ticket?.status || '';
          this.reactivationReason = '';
          this.notifyService.showSuccess(
            res.message || 'Account reactivated successfully.',
            'Account Reactivation'
          );
        },
        error: (err) => {
          this.notifyService.showError(
            err?.error?.message || 'Failed to reactivate the account.',
            'Account Reactivation'
          );
          this.reactivatingAccount = false;
        },
        complete: () => {
          this.reactivatingAccount = false;
        }
      });
  }

  updateStatus(): void {
    if (!this.ticket || !this.selectedStatus || this.savingStatus) return;

    this.savingStatus = true;

    this.supportService.updateAdminTicketStatus(this.ticket.id, this.selectedStatus).subscribe({
      next: (res) => {
        this.ticket = res.data || this.ticket;
        this.selectedStatus = this.ticket?.status || '';
        this.notifyService.showSuccess(
          res.message || 'Ticket status updated successfully.',
          'Success'
        );
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.message || 'Failed to update ticket status.',
          'Error'
        );
        this.savingStatus = false;
      },
      complete: () => {
        this.savingStatus = false;
      }
    });
  }

  parseContext(json?: string | null): any | null {
    if (!json) return null;

    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }
}